import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";

import {
  MapPin,
  Mail,
  Phone,
} from "lucide-react";

import Header from "../../components/Header";
import Footer from "../../components/Footer";

import API_BASE_URL from "../../api/api"
// const API_BASE_URL =
//   window.location.hostname === "localhost" ||
//   window.location.hostname === "127.0.0.1"
//     ? "http://127.0.0.1:8000/api"
//     : "https://api.kunainital.ac.in/api";

const FACULTIES_API = `${API_BASE_URL}/faculties`;
const DEPARTMENTS_API = `${API_BASE_URL}/departments`;

const slugify = (value = "") =>
  value
    .toString()
    .toLowerCase()
    .trim()
    .replace(/&/g, "and")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const DepartmentDetail = () => {
  const { facultySlug, slug } = useParams();
  const navigate = useNavigate();

  const [faculty, setFaculty] = useState(null);
  const [department, setDepartment] = useState(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const fetchDepartment = async () => {
      try {
        setLoading(true);
        setError("");

        const [
          facultyResponse,
          departmentResponse,
        ] = await Promise.all([
          fetch(FACULTIES_API),
          fetch(DEPARTMENTS_API),
        ]);

        if (!facultyResponse.ok) {
          throw new Error(
            `Faculty API Error: ${facultyResponse.status}`
          );
        }

        if (!departmentResponse.ok) {
          throw new Error(
            `Department API Error: ${departmentResponse.status}`
          );
        }

        const facultyResult =
          await facultyResponse.json();

        const departmentResult =
          await departmentResponse.json();

        if (
          !facultyResult.status ||
          !Array.isArray(facultyResult.data)
        ) {
          throw new Error(
            "Invalid faculty API response"
          );
        }

        if (
          !departmentResult.status ||
          !Array.isArray(departmentResult.data)
        ) {
          throw new Error(
            "Invalid department API response"
          );
        }

        /*
         * FIND FACULTY
         */
        const selectedFaculty =
          facultyResult.data.find(
            (item) =>
              Number(item.status) === 1 &&
              slugify(item.name) === facultySlug
          );

        if (!selectedFaculty) {
          setError("Faculty not found.");
          setFaculty(null);
          setDepartment(null);
          return;
        }

        /*
         * FIND DEPARTMENT
         */
        const selectedDepartment =
          departmentResult.data.find(
            (item) =>
              Number(item.status) === 1 &&
              slugify(item.name) === slug &&
              Number(item.faculty_id) ===
                Number(selectedFaculty.id)
          );

        if (!selectedDepartment) {
          setError("Department not found.");
          setFaculty(selectedFaculty);
          setDepartment(null);
          return;
        }

        setFaculty(selectedFaculty);
        setDepartment(selectedDepartment);

      } catch (err) {
        console.error(
          "Department Detail Error:",
          err
        );

        setError(
          err.message ||
            "Unable to load department details."
        );
      } finally {
        setLoading(false);
      }
    };

    if (facultySlug && slug) {
      fetchDepartment();
    }
  }, [facultySlug, slug]);

  /*
   * LOADING
   */
  if (loading) {
    return (
      <div className="min-h-screen bg-[var(--paper)]">
        <Header />

        <div className="min-h-[500px] flex items-center justify-center">
          <div className="text-center">

            <div className="w-10 h-10 border-4 border-gray-200 border-t-[#B5121B] rounded-full animate-spin mx-auto mb-4" />

            <p className="text-gray-600 text-lg font-gill">
              Loading department...
            </p>

          </div>
        </div>

        <Footer />
      </div>
    );
  }

  /*
   * ERROR
   */
  if (error || !department) {
    return (
      <div className="min-h-screen bg-[var(--paper)]">
        <Header />

        <section className="min-h-[500px] flex items-center justify-center px-6">

          <div className="text-center max-w-xl">

            <h1 className="text-3xl font-bold text-[#08245c] mb-3 font-gill">
              Department Not Found
            </h1>

            <p className="text-gray-600 font-gill">
              {error ||
                "The requested department could not be found."}
            </p>

            <button
              onClick={() => navigate(-1)}
              className="mt-6 inline-flex items-center font-gill text-[16px] bg-[#8B1E1E] text-white px-5 py-2 rounded-md font-semibold hover:bg-[#6F1717] transition-colors duration-200"
            >
              ← Go Back
            </button>

          </div>

        </section>

        <Footer />
      </div>
    );
  }

  /*
   * API DATA
   */

  const departmentName =
    department.name ||
    department.title ||
    "Department";

  /*
   * Faculty name directly from
   * Faculties API
   */
  const facultyName =
    faculty?.name ||
    department.faculty_name ||
    "Faculty";

  const description =
    department.description ||
    department.details ||
    department.content ||
    department.about ||
    "";

  const vision =
    department.vision || "";

  const mission =
    department.mission || "";

  const objectives =
    department.objectives || "";

  const address =
    department.address ||
    department.location ||
    "";

  const email =
    department.email ||
    department.contact_email ||
    "";

  const phone =
    department.phone ||
    department.contact_number ||
    "";

  /*
   * SAME IMAGE LOGIC AS
   * FacultiesOfArtsPages
   */
  const image =
    department.image ||
    department.image_url ||
    "/images/campusKumaun.avif";

  /*
   * HTML CONTENT
   */
  const renderContent = (content) => {
    if (!content) return null;

    return (
      <div
        className="text-[16px] leading-8 text-gray-700 font-gill text-justify"
        dangerouslySetInnerHTML={{
          __html: content,
        }}
      />
    );
  };

  return (
    <div className="min-h-screen bg-[var(--paper)]">

      <Header />

      {/* ================= HERO ================= */}

      <section className="relative h-[250px] overflow-hidden">

        {/* DEPARTMENT IMAGE */}

        <img
          src={image}
          alt={departmentName}
          className="absolute inset-0 w-full h-full object-cover"
          onError={(event) => {
            event.currentTarget.src =
              "/images/campusKumaun.avif";
          }}
        />

        {/* OVERLAY */}

        <div className="absolute inset-0 bg-gradient-to-r from-[#d1fae5]/95 via-[#a7f3d0]/80 to-[#ecfdf5]/40" />

        <div className="relative z-10 max-w-7xl mx-auto h-full px-6 flex items-center">

          <div className="max-w-5xl">

            {/* BREADCRUMB */}

            <div className="flex flex-wrap items-center gap-2 text-sm mb-5 font-gill">

              <span className="text-[#123B5D] text-lg">
                Faculties
              </span>

              <span className="text-[#123B5D] text-lg">
                /
              </span>

              <span className="text-[#123B5D] text-lg">
                {facultyName}
              </span>

              <span className="text-[#123B5D] text-lg">
                /
              </span>

              <span className="text-[#123B5D] text-lg">
                {departmentName}
              </span>

            </div>

            {/* DEPARTMENT TITLE */}

            <h1 className="text-4xl md:text-5xl font-bold leading-tight text-[#064E3B] font-gill">
              {departmentName.toUpperCase()}
            </h1>

          </div>

        </div>

      </section>


      {/* ================= MAIN CONTENT ================= */}

      <section className="bg-white py-12">

        <div className="max-w-7xl mx-auto px-6">

          {/* BACK BUTTON */}

          <button
            onClick={() => navigate(-1)}
            className="mb-6 inline-flex items-center font-gill text-[16px] bg-[#8B1E1E] text-white px-5 py-2 rounded-md font-semibold hover:bg-[#6F1717] transition-colors duration-200"
          >
            ← Back
          </button>


          {/* ================= ABOUT ================= */}

          <div className="mb-10">

            <h2 className="text-3xl md:text-4xl font-bold text-[#08245c] mb-6 font-gill">
              About the {departmentName}
            </h2>

            {description ? (

              <div className="text-[16px] leading-8 text-gray-700 text-lg font-gill">

                {renderContent(description)}

              </div>

            ) : (

              <p className="text-gray-500 text-lg font-gill">
                Department description is not available.
              </p>

            )}

          </div>


          {/* ================= VISION ================= */}

          {vision && (
            <div className="mb-8">

              <h3 className="text-2xl font-bold text-[#08245c] mb-3 font-gill">
                Vision
              </h3>

              <div className="text-[16px] leading-8 text-gray-700 text-lg font-gill">

                {renderContent(vision)}

              </div>

            </div>
          )}


          {/* ================= MISSION ================= */}

          {mission && (
            <div className="mb-8">

              <h3 className="text-2xl font-bold text-[#08245c] mb-3 font-gill">
                Mission
              </h3>

              <div className="text-[16px] leading-8 text-gray-700 text-lg font-gill">

                {renderContent(mission)}

              </div>

            </div>
          )}


          {/* ================= OBJECTIVES ================= */}

          {objectives && (
            <div className="mb-8">

              <h3 className="text-2xl font-bold text-[#08245c] mb-3 font-gill">
                Objectives
              </h3>

              <div className="text-[16px] leading-8 text-gray-700 text-lg font-gill">

                {renderContent(objectives)}

              </div>

            </div>
          )}


          {/* ================= CONTACT ================= */}

          {(address || email || phone) && (
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">

              {/* ADDRESS */}

              {address && (
                <div className="border border-gray-200 rounded-lg p-5">

                  <div className="flex items-start gap-3">

                    <MapPin
                      size={22}
                      className="text-[#B5121B] flex-shrink-0"
                    />

                    <div>

                      <h4 className="font-bold text-[#08245c] mb-1 font-gill">
                        Address
                      </h4>

                      <p className="text-gray-600 text-sm font-gill">
                        {address}
                      </p>

                    </div>

                  </div>

                </div>
              )}


              {/* EMAIL */}

              {email && (
                <div className="border border-gray-200 rounded-lg p-5">

                  <div className="flex items-start gap-3">

                    <Mail
                      size={22}
                      className="text-[#B5121B] flex-shrink-0"
                    />

                    <div>

                      <h4 className="font-bold text-[#08245c] mb-1 font-gill">
                        Email
                      </h4>

                      <a
                        href={`mailto:${email}`}
                        className="text-gray-600 text-sm hover:text-[#B5121B] font-gill"
                      >
                        {email}
                      </a>

                    </div>

                  </div>

                </div>
              )}


              {/* PHONE */}

              {phone && (
                <div className="border border-gray-200 rounded-lg p-5">

                  <div className="flex items-start gap-3">

                    <Phone
                      size={22}
                      className="text-[#B5121B] flex-shrink-0"
                    />

                    <div>

                      <h4 className="font-bold text-[#08245c] mb-1 font-gill">
                        Contact
                      </h4>

                      <a
                        href={`tel:${phone}`}
                        className="text-gray-600 text-sm hover:text-[#B5121B] font-gill"
                      >
                        {phone}
                      </a>

                    </div>

                  </div>

                </div>
              )}

            </div>
          )}

        </div>

      </section>

      <Footer />

    </div>
  );
};

export default DepartmentDetail;